import compact from 'lodash/compact';

import {publicTrackFixedLayerIds} from './fixedLayerIds';

export default function getPublicTracksMapboxStyle({
    showPublicTracks,
    interaction,
    isPrinting,
    selections,
}: {
    showPublicTracks: undefined | boolean;
    interaction: InteractionState;
    isPrinting: boolean;
    selections: SelectionState[];
}): mapboxgl.Style | undefined {
    if (!showPublicTracks) return undefined;

    const colorExpression: mapboxgl.Expression = ['coalesce', ['get', 'color'], '#00FF00'];

    return {
        version: 8,
        metadata: {
            id: 'publicTracks',
        },
        sources: {
            source: {
                type: 'vector',
                tiles: [`${window.location.origin}/api/public/tiles/{z}/{x}/{y}.mvt`],
                minzoom: 8,
                maxzoom: 11,
            },
        },
        layers: [
            {
                id: publicTrackFixedLayerIds.casing,
                type: 'line',
                source: 'source',
                'source-layer': 'tracks',
                paint: {
                    'line-color': '#fff',
                    'line-width': getFocusExpression(interaction, selections, 3, 5, 3, 3),
                    'line-opacity': 0.3,
                },
            },
            {
                id: publicTrackFixedLayerIds.line,
                type: 'line',
                source: 'source',
                'source-layer': 'tracks',
                paint: {
                    'line-color': getFocusExpression<string>(
                        interaction,
                        selections,
                        colorExpression,
                        colorExpression,
                        '#3F8348',
                        '#3F8348'
                    ),
                    'line-width': getFocusExpression(interaction, selections, 3, 5, 3, 3),
                    'line-opacity': getFocusExpression(
                        interaction,
                        selections,
                        1,
                        1,
                        isPrinting ? 0 : 0.3,
                        0.3
                    ),
                },
            },
            {
                id: publicTrackFixedLayerIds.symbol,
                type: 'symbol',
                source: 'source',
                'source-layer': 'tracks',
                layout: {
                    'icon-image': 'trailDirectionIcon',
                    'icon-size': 0.6,
                    'icon-allow-overlap': false,
                    'symbol-placement': 'line',
                    'symbol-spacing': 80,
                    'icon-keep-upright': false,
                    'icon-ignore-placement': false,
                },
                paint: {
                    'icon-opacity': 0.65,
                },
                filter: [
                    'all',
                    getFocusExpression(interaction, selections, false, true, false, false),
                ],
            },
        ],
    };
}

// eslint-disable-next-line max-params
function getFocusExpression<T>(
    interaction: InteractionState,
    selections: SelectionState[],
    hovered: T | mapboxgl.Expression,
    focused: T | mapboxgl.Expression,
    blurred: T | mapboxgl.Expression,
    normal: T | mapboxgl.Expression
): T | mapboxgl.Expression {
    if (
        interaction.type === 'normal' &&
        interaction.subtype === 'hovering' &&
        interaction.feature.properties?.id
    ) {
        return [
            'case',
            [
                '==',
                ['coalesce', ['get', 'localId'], ['get', 'id']],
                interaction.feature.properties.id,
            ],
            hovered,
            blurred,
        ];
    } else if (selections.length) {
        const inner = compact(
            selections.map((selection) => {
                if (selection.type === 'track') {
                    return ['==', ['get', 'localId'], selection.localId];
                } else {
                    return undefined;
                }
            })
        );
        if (!inner.length) return blurred;
        return ['case', ['any', ...inner], focused, blurred];
    } else if (interaction.type !== 'normal') {
        return blurred;
    } else {
        return normal;
    }
}
